import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConfirmDialog, Typography } from '@occc/ui-design-system';
import { Page, snackbar, useAppLayoutContextConsumer } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { useConfigureReferralProgramMutation } from '@occc/graphql';
import { useConfig } from '@occc/config';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useChainConfiguration, useChainId } from '@occc-app/Chain/hooks';

import { ReferralProgramConfigurationPage } from './referral-program-configuration.page';
import { referralProgramConfigurationMessages } from './referral-program-configuration.messages';
import { breadcrumbs } from './referral-program-configuration.constants';
import {
  createConfigureReferralProgramInput,
  createReferralProgramConfigurationInitialValues,
} from './referral-program-configuration.utils';
import { ReferralProgramConfigurationValues } from './referral-program-configuration.types';

import { routeCreator } from '../../referral-program.router';

export const ReferralProgramConfigurationController = () => {
  useAppLayoutContextConsumer(breadcrumbs);
  const navigate = useNavigate();
  const chainId = useChainId();
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    description?: string;
    onConfirm?: () => Promise<void>;
    confirmButtonText?: string;
  }>({ isOpen: false });

  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.REFERRAL_PROGRAM_EDIT);
  const { config } = useConfig();
  const subBrands = config.subBrands?.enabled ? config.subBrands.list : null;
  const { referralProgramConfiguration } = useChainConfiguration();
  const initialValues = useMemo(
    () => createReferralProgramConfigurationInitialValues(referralProgramConfiguration, subBrands),
    [referralProgramConfiguration, subBrands],
  );

  const [configureReferralProgram, { loading }] = useConfigureReferralProgramMutation({
    refetchQueries: ['chains'],
    onCompleted: () => {
      snackbar.success(referralProgramConfigurationMessages.configurationSuccessMessage);
      navigate(routeCreator.details());
    },
    onError: (error) => snackbar.error(getErrorMessage(error)),
  });

  const configure = useCallback(
    async (values: ReferralProgramConfigurationValues) => {
      try {
        const input = createConfigureReferralProgramInput({ chainId, values });
        await configureReferralProgram({ variables: { input } });
      } catch (e) {
        snackbar.error('Something went wrong');
      }
    },
    [configureReferralProgram, chainId],
  );

  const handleSubmit = useCallback(
    async (values: ReferralProgramConfigurationValues) => {
      if (!referralProgramConfiguration) {
        return configure(values);
      }

      // Display confirmation dialog if referral program is live or already created
      setConfirmationDialog({
        isOpen: true,
        description: referralProgramConfigurationMessages.submitConfirmationDialogDescription,
        onConfirm: () => {
          setConfirmationDialog((state) => ({ ...state, isOpen: false }));
          return configure(values);
        },
      });
    },
    [referralProgramConfiguration, configure],
  );

  const handleCancel = useCallback(
    (params: { isDirty: boolean }) => {
      const { isDirty } = params;
      if (!isDirty) {
        return navigate(routeCreator.details());
      }

      // Display confirmation dialog if there are unsaved changes
      setConfirmationDialog({
        isOpen: true,
        description: referralProgramConfigurationMessages.cancelConfirmationDialogDescription,
        confirmButtonText:
          referralProgramConfigurationMessages.cancelConfirmationDialogConfirmButtonText,
        onConfirm: async () => {
          setConfirmationDialog({ isOpen: false });
          return navigate(routeCreator.details());
        },
      });
    },
    [navigate],
  );

  return (
    <Page
      pageHeaderProps={{
        title: referralProgramConfigurationMessages.pageTitle,
        subtitle: referralProgramConfigurationMessages.pageSubtitle,
      }}
      browserTabTitle={referralProgramConfigurationMessages.pageTitle}
      containerProps={{ variant: 'md' }}
    >
      <ReferralProgramConfigurationPage
        initialValues={initialValues}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        isLoading={loading}
        isEditable={isUserPermittedToEdit}
        subBrands={subBrands}
      />
      <ConfirmDialog
        open={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog((state) => ({ ...state, isOpen: false }))}
        confirmButtonProps={{
          onClick: confirmationDialog.onConfirm,
        }}
        confirmButtonText={
          confirmationDialog.confirmButtonText ||
          referralProgramConfigurationMessages.confirmationDialogConfirmButtonText
        }
        declineButtonText={referralProgramConfigurationMessages.confirmationDialogCancelButtonText}
      >
        <Typography>{confirmationDialog.description}</Typography>
      </ConfirmDialog>
    </Page>
  );
};
